import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2,
  Check,
  ChevronRight,
  ChevronDown,
  ArrowUpRight,
  LayoutDashboard,
  Zap,
  Plus,
  Info,
  CheckCircle2,
  X,
  Loader2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { cn } from "../lib/utils";
import { useToast } from "../hooks/use-toast";
import apiClient from "../lib/api";

// Categories are now fetched from the database via API

interface OnboardingModalProps {
  open: boolean;
  onComplete: () => void;
  categories?: any[];
  setCategories?: (cats: any[]) => void;
}

export default function OnboardingModal({ 
  open, 
  onComplete,
  categories = [],
  setCategories
}: OnboardingModalProps) {
  const [step, setStep] = useState<"intro" | "profile" | "team" | "priorities" | "finish">("intro");
  const [onboardingData, setOnboardingData] = useState({
    businessRole: "",
    consultantType: "",
    constructionRole: "",
    strategies: [] as string[],
    assets: [] as string[],
    contexts: [] as string[],
    urgencyTimeHorizon: [] as string[],
    invites: [{ email: "", role: "Analyst" }],
    priorities: [] as string[] // Will store category slugs
  });
  
  // Fetch default categories from database
  const [defaultCategories, setDefaultCategories] = useState<any[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState<string | null>(null);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  
  useEffect(() => {
    if (open && step === "priorities") {
      fetchDefaultCategories();
    }
    if (open) {
      fetchCompanyInfo();
    }
  }, [open, step]);
  
  const fetchCompanyInfo = async () => {
    try {
      const companyInfo = await apiClient.companies.getInfo();
      setSubscriptionTier(companyInfo.subscription_tier || null);
    } catch (error) {
      console.error('Failed to fetch company info:', error);
    }
  };
  
  const handleUpgradeToOrg = async () => {
    setIsUpgrading(true);
    try {
      await apiClient.stripe.upgradeToOrgPlan();
      toast({
        title: "Upgrade successful!",
        description: "Your subscription has been upgraded to Org plan. You can now invite team members.",
      });
      // Refresh company info to update subscription tier
      await fetchCompanyInfo();
      setShowUpgradeDialog(false);
    } catch (error: any) {
      console.error('Failed to upgrade subscription:', error);
      toast({
        title: "Upgrade failed",
        description: error.response?.data?.detail || error.message || "Failed to upgrade subscription. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpgrading(false);
    }
  };
  
  const fetchDefaultCategories = async () => {
    try {
      setLoadingCategories(true);
      const categories = await apiClient.topics.getOnboardingDefaults();
      setDefaultCategories(categories);
    } catch (error) {
      console.error('Failed to fetch default categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };
  
  // Track custom "Other" values for each field
  const [customStrategy, setCustomStrategy] = useState("");
  const [customAsset, setCustomAsset] = useState("");
  const [customContext, setCustomContext] = useState("");
  const [customUrgency, setCustomUrgency] = useState("");
  const [customBusinessRole, setCustomBusinessRole] = useState("");
  const { toast } = useToast();
  
  // State to control dropdown open/close for multi-select dropdowns
  const [strategyDropdownOpen, setStrategyDropdownOpen] = useState(false);
  const [assetDropdownOpen, setAssetDropdownOpen] = useState(false);
  const [contextDropdownOpen, setContextDropdownOpen] = useState(false);
  const [urgencyDropdownOpen, setUrgencyDropdownOpen] = useState(false);
  const [businessRoleDropdownOpen, setBusinessRoleDropdownOpen] = useState(false);

  // Reset step when modal opens
  useEffect(() => {
    if (open) {
      setStep("intro");
      setOnboardingData({
        businessRole: "",
        consultantType: "",
        constructionRole: "",
        strategies: [],
        assets: [],
        contexts: [],
        urgencyTimeHorizon: [],
        invites: [{ email: "", role: "Analyst" }],
        priorities: []
      });
      // Reset dropdown states
      setStrategyDropdownOpen(false);
      setAssetDropdownOpen(false);
      setContextDropdownOpen(false);
      setUrgencyDropdownOpen(false);
      setBusinessRoleDropdownOpen(false);
      // Reset custom "Other" values
      setCustomStrategy("");
      setCustomAsset("");
      setCustomContext("");
      setCustomUrgency("");
      setCustomBusinessRole("");
    }
  }, [open]);

  const handleNext = async () => {
    if (step === "intro") {
      setStep("profile");
    } else if (step === "profile") {
      // Skip team step for solo plan users
      if (subscriptionTier === "solo") {
        setStep("priorities");
      } else {
        setStep("team");
      }
    } else if (step === "team") {
      setStep("priorities");
    } else if (step === "priorities") {
      // Save priorities and complete onboarding
      try {
        // Build business profile object with all 5 fields
        const businessProfile: any = {
          businessRole: onboardingData.businessRole || null,
          strategies: [
            ...onboardingData.strategies,
            ...(customStrategy.trim() ? [customStrategy.trim()] : [])
          ],
          assets: [
            ...onboardingData.assets,
            ...(customAsset.trim() ? [customAsset.trim()] : [])
          ],
          contexts: [
            ...(onboardingData.contexts || []),
            ...(customContext.trim() ? [customContext.trim()] : [])
          ],
          urgencyTimeHorizon: [
            ...onboardingData.urgencyTimeHorizon,
            ...(customUrgency.trim() ? [customUrgency.trim()] : [])
          ]
        };
        
        console.log('[Onboarding] Submitting business profile:', JSON.stringify(businessProfile, null, 2));
        console.log('[Onboarding] Selected priorities:', onboardingData.priorities);
        console.log('[Onboarding] Business profile keys:', Object.keys(businessProfile));
        console.log('[Onboarding] Business role:', businessProfile.businessRole);
        console.log('[Onboarding] Strategies:', businessProfile.strategies);
        console.log('[Onboarding] Assets:', businessProfile.assets);
        console.log('[Onboarding] Contexts:', businessProfile.contexts);
        console.log('[Onboarding] Urgency:', businessProfile.urgencyTimeHorizon);
        
        // Map priorities to category slugs (if we need to)
        // For now, just save the onboarding as complete
        const response = await apiClient.topics.completeOnboarding(onboardingData.priorities, businessProfile);
        console.log('[Onboarding] Response from server:', response);
        setStep("finish");
      } catch (error: any) {
        console.error('❌ Failed to save onboarding:', error);
        console.error('❌ Error details:', error.response?.data || error.message);
        toast({
          title: "Error",
          description: error.response?.data?.detail || error.message || "Failed to save your preferences. Please try again.",
          variant: "destructive",
        });
      }
    } else {
      onComplete();
    }
  };

  const togglePriority = (id: string) => {
    const next = onboardingData.priorities.includes(id) 
      ? onboardingData.priorities.filter(x => x !== id) 
      : [...onboardingData.priorities, id];
    setOnboardingData({...onboardingData, priorities: next});
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-5xl p-0 gap-0 overflow-hidden border-none shadow-2xl h-[700px] flex">
        {/* Left Side - Visual - Keeping original design with dots */}
        <div className="w-[340px] bg-[#1A1C1E] relative flex-col justify-between p-8 overflow-hidden hidden md:flex shrink-0">
          <div className="absolute inset-0 opacity-10" style={{ 
            backgroundImage: "radial-gradient(#bada55 1.5px, transparent 1.5px)", 
            backgroundSize: "24px 24px" 
          }} />
          
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-10 w-10 bg-white/5 backdrop-blur-md rounded-xl flex items-center justify-center border border-white/10">
                <Building2 className="h-5 w-5 text-primary" />
              </div>
              <h3 className="text-white font-bold text-xl tracking-tight">Cityscrape</h3>
            </div>
            <p className="text-neutral-200 font-medium text-sm mb-4">Municipal intelligence, reimagined.</p>
            {step === "priorities" && (
              <div className="text-neutral-300 text-xs leading-relaxed space-y-2">
                <p>
                  <span className="font-semibold text-white">Topics</span> are categories that help Cityscrape identify and organize policy changes, approvals, and decisions from municipal meetings.
                </p>
                <p>
                  Select the topics that matter most to your work. These selections help us prioritize items in your Industry Pulse feed.
                </p>
                <p className="text-neutral-400 italic">
                  You can create custom topics later to track specific themes, policies, or programs.
                </p>
              </div>
            )}
          </div>

          <div className="relative z-10 space-y-4">
            <div className="flex items-center gap-3 text-sm text-white/80">
              <div className={cn("h-2 w-2 rounded-full transition-all duration-300", step === "intro" ? "bg-primary scale-125" : "bg-white/20")} />
              <span className={cn(step === "intro" ? "text-primary font-medium" : "text-white/40")}>Introduction</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-white/80">
              <div className={cn("h-2 w-2 rounded-full transition-all duration-300", step === "profile" ? "bg-primary scale-125" : "bg-white/20")} />
              <span className={cn(step === "profile" ? "text-primary font-medium" : "text-white/40")}>Business Profile</span>
            </div>
            {subscriptionTier !== "solo" && (
              <div className="flex items-center gap-3 text-sm text-white/80">
                <div className={cn("h-2 w-2 rounded-full transition-all duration-300", step === "team" ? "bg-primary scale-125" : "bg-white/20")} />
                <span className={cn(step === "team" ? "text-primary font-medium" : "text-white/40")}>Invite Team</span>
              </div>
            )}
            <div className="flex items-center gap-3 text-sm text-white/80">
              <div className={cn("h-2 w-2 rounded-full transition-all duration-300", step === "priorities" ? "bg-primary scale-125" : "bg-white/20")} />
              <span className={cn(step === "priorities" ? "text-primary font-medium" : "text-white/40")}>Priorities</span>
            </div>
          </div>
        </div>

        {/* Right Side - Content */}
        <div className="flex-1 flex flex-col h-full bg-white dark:bg-neutral-950 overflow-hidden relative">
          <style dangerouslySetInnerHTML={{ __html: `
            .hide-scrollbar::-webkit-scrollbar { display: none; }
            .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
          `}} />
          <AnimatePresence mode="wait">
            {step === "intro" && (
              <motion.div 
                key="intro"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="flex-1 flex flex-col p-10 h-full overflow-hidden"
              >
                 <div className="mb-8">
                   <Badge className="mb-4 bg-primary/10 text-primary border-none">Intelligence System</Badge>
                   <h2 className="text-3xl font-bold text-neutral-900 dark:text-white mb-2">Set your monitoring strategy</h2>
                   <p className="text-neutral-500 leading-relaxed text-base">
                     We'll use this to personalize your Industry Pulse, alerts, and property intelligence.
                   </p>
                 </div>

                 <div className="grid grid-cols-2 gap-4 mb-8">
                   <div className="p-4 rounded-xl bg-neutral-50 dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 flex flex-col items-center text-center">
                     <div className="h-10 w-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mb-3">
                       <LayoutDashboard className="h-5 w-5" />
                     </div>
                     <h4 className="font-semibold text-sm">Personalized Feed</h4>
                     <p className="text-[11px] text-neutral-500 mt-1">Industry Pulse, ranked by relevance to your role and priorities.</p>
                   </div>
                   <div className="p-4 rounded-xl bg-neutral-50 dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 flex flex-col items-center text-center">
                     <div className="h-10 w-10 rounded-full bg-green-100 text-green-600 flex items-center justify-center mb-3">
                       <Zap className="h-5 w-5" />
                     </div>
                     <h4 className="font-semibold text-sm">Team Access</h4>
                     <p className="text-[11px] text-neutral-500 mt-1">Collaborate with your team using shared monitoring and alerts.</p>
                   </div>
                 </div>

                 <div className="mt-auto flex flex-col items-center gap-4 pt-6">
                    <p className="text-[11px] text-neutral-400">
                      🔒 You can change all of this later. Nothing here limits what you can access.
                    </p>
                    <Button onClick={handleNext} className="w-full h-12 px-8 font-semibold gap-2 shadow-lg shadow-primary/20">
                      Get Started <ChevronRight className="h-4 w-4" />
                    </Button>
                 </div>
              </motion.div>
            )}

            {step === "profile" && (
              <motion.div 
                key="profile"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="flex-1 flex flex-col p-10 h-full overflow-hidden"
              >
                 <div className="mb-4 shrink-0">
                   <Badge className="mb-2 bg-primary/10 text-primary border-none">Step 1 of 3</Badge>
                   <h2 className="text-2xl font-bold text-neutral-900 dark:text-white">Business Profile</h2>
                   <p className="text-neutral-500 text-sm mt-1">Tell us about your role and strategy.</p>
                 </div>

                 <div className="space-y-4 overflow-y-auto pr-2 hide-scrollbar pb-6">
                   <div className="space-y-1.5">
                     <Label className="text-sm font-semibold">Business Role</Label>
                     <DropdownMenu open={businessRoleDropdownOpen} onOpenChange={setBusinessRoleDropdownOpen}>
                       <DropdownMenuTrigger asChild>
                         <Button
                           variant="outline"
                           className="w-full justify-between text-left font-normal h-10"
                         >
                           <span>{onboardingData.businessRole || "Select role"}</span>
                           <ChevronDown className="h-4 w-4 opacity-50" />
                         </Button>
                       </DropdownMenuTrigger>
                       <DropdownMenuContent className="min-w-[var(--radix-dropdown-menu-trigger-width)] w-[var(--radix-dropdown-menu-trigger-width)]">
                         {[
                           "Developer (owner/operator)", 
                           "Developer (fee/service)", 
                           "Investor / Owner", 
                           "Lender / Capital partner",
                           "Broker / Advisor",
                           "Public sector (municipality/agency)",
                           "Non-profit",
                           "Professional services (consultant)", 
                           "Construction / delivery (contractor/vendor)"
                         ].map(role => {
                           const isSelected = onboardingData.businessRole === role;
                           return (
                            <DropdownMenuCheckboxItem
                              key={role}
                              checked={isSelected}
                              onCheckedChange={() => {
                                // Clear custom field when selecting predefined option
                                setCustomBusinessRole("");
                                setOnboardingData({...onboardingData, businessRole: role});
                                // Close dropdown after single selection
                                setBusinessRoleDropdownOpen(false);
                              }}
                              className="relative pr-8 pl-2 [&>span:first-child]:hidden"
                            >
                              <span className="flex-1">{role}</span>
                              {isSelected && (
                                <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
                                  <Check className="h-4 w-4 text-black" />
                                </span>
                              )}
                            </DropdownMenuCheckboxItem>
                           );
                         })}
                         <div className="relative flex items-center rounded-sm py-1.5 pl-2 pr-2 text-sm outline-none">
                           <Input
                             placeholder="Please specify..."
                             value={customBusinessRole}
                             onChange={(e) => {
                               const value = e.target.value;
                               setCustomBusinessRole(value);
                               // Only set if there's text
                               if (value.trim()) {
                                 setOnboardingData({...onboardingData, businessRole: value.trim()});
                               } else {
                                 setOnboardingData({...onboardingData, businessRole: ""});
                               }
                             }}
                             onClick={(e) => e.stopPropagation()}
                             onKeyDown={(e) => e.stopPropagation()}
                             onFocus={(e) => e.stopPropagation()}
                             onMouseDown={(e) => e.stopPropagation()}
                             className="h-7 text-sm flex-1"
                           />
                         </div>
                       </DropdownMenuContent>
                     </DropdownMenu>
                   </div>

                   {onboardingData.businessRole === "Professional services (consultant)" && (
                     <motion.div 
                       initial={{ opacity: 0, height: 0 }} 
                       animate={{ opacity: 1, height: "auto" }}
                       className="space-y-1.5"
                     >
                       <Label className="text-sm font-semibold">Consultant Type</Label>
                       <Select onValueChange={(v) => setOnboardingData({...onboardingData, consultantType: v})} value={onboardingData.consultantType}>
                         <SelectTrigger className="h-10">
                           <SelectValue placeholder="Select consultant type" />
                         </SelectTrigger>
                         <SelectContent>
                           {["Planner/Planning consultant", "Architect/Urban designer", "Land use lawyer", "Engineer (civil/structural)", "Transportation/traffic", "Environmental", "Survey/geomatics", "Other"].map(type => (
                             <SelectItem key={type} value={type}>{type}</SelectItem>
                           ))}
                         </SelectContent>
                       </Select>
                     </motion.div>
                   )}

                   {onboardingData.businessRole === "Construction / delivery (contractor/vendor)" && (
                     <motion.div 
                       initial={{ opacity: 0, height: 0 }} 
                       animate={{ opacity: 1, height: "auto" }}
                       className="space-y-1.5"
                     >
                       <Label className="text-sm font-semibold">Construction Role</Label>
                       <Select onValueChange={(v) => setOnboardingData({...onboardingData, constructionRole: v})} value={onboardingData.constructionRole}>
                         <SelectTrigger className="h-10">
                           <SelectValue placeholder="Select construction role" />
                         </SelectTrigger>
                         <SelectContent>
                           {["Builder/GC", "Construction manager", "Trades/subcontractor", "RFP/tender responder", "Other"].map(role => (
                             <SelectItem key={role} value={role}>{role}</SelectItem>
                           ))}
                         </SelectContent>
                       </Select>
                     </motion.div>
                   )}

                   <div className="space-y-1.5">
                     <div className="flex items-center justify-between">
                       <Label className="text-sm font-semibold">Strategy <span className="text-[10px] text-neutral-400 font-normal">(Recommended 2-3)</span></Label>
                       {onboardingData.strategies.length > 3 && (
                         <span className="text-[10px] text-amber-600 font-medium italic">⚠️ Over recommended</span>
                       )}
                     </div>
                     <DropdownMenu open={strategyDropdownOpen} onOpenChange={setStrategyDropdownOpen}>
                       <DropdownMenuTrigger asChild>
                         <Button
                           variant="outline"
                           className="w-full justify-between text-left font-normal h-10"
                         >
                          <span className={onboardingData.strategies.length > 0 ? "text-foreground" : "text-muted-foreground"}>
                            {onboardingData.strategies.length > 0 
                              ? `${onboardingData.strategies.length} selected` 
                              : "Select strategies..."}
                          </span>
                          <ChevronDown className="h-4 w-4 opacity-50" />
                         </Button>
                       </DropdownMenuTrigger>
                       <DropdownMenuContent className="min-w-[var(--radix-dropdown-menu-trigger-width)] w-[var(--radix-dropdown-menu-trigger-width)]">
                         {["Acquire / invest", "Entitle / approvals", "Build / deliver", "Hold / operate", "Sell / dispose", "Advisory/services"].map(s => {
                           const isSelected = onboardingData.strategies.includes(s);
                           return (
                             <DropdownMenuCheckboxItem
                               key={s}
                               checked={isSelected}
                               onCheckedChange={() => {
                                 if (!onboardingData.strategies.includes(s)) {
                                   setOnboardingData({...onboardingData, strategies: [...onboardingData.strategies, s]});
                                 } else {
                                   setOnboardingData({...onboardingData, strategies: onboardingData.strategies.filter(x => x !== s)});
                                 }
                               }}
                               onSelect={(e) => e.preventDefault()}
                               className="relative pr-8 pl-2 [&>span:first-child]:hidden"
                             >
                               <span className="flex-1">{s}</span>
                               {isSelected && (
                                 <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
                                   <Check className="h-4 w-4 text-black" />
                                 </span>
                               )}
                             </DropdownMenuCheckboxItem>
                           );
                         })}
                         <div className="relative flex items-center rounded-sm py-1.5 pl-2 pr-2 text-sm outline-none">
                           <Input
                             placeholder="Please specify..."
                             value={customStrategy}
                             onChange={(e) => {
                               const value = e.target.value;
                               setCustomStrategy(value);
                               
                               // Find any existing custom strategy (one that's not in the predefined list)
                               const predefined = ["Acquire / invest", "Entitle / approvals", "Build / deliver", "Hold / operate", "Sell / dispose", "Advisory/services"];
                               const currentStrategies = onboardingData.strategies;
                               const existingCustom = currentStrategies.find(s => !predefined.includes(s));
                               
                               // Remove the existing custom value if it exists
                               const strategiesWithoutCustom = existingCustom 
                                 ? currentStrategies.filter(x => x !== existingCustom)
                                 : currentStrategies;
                               
                               // Only add if there's text
                               if (value.trim()) {
                                 setOnboardingData({...onboardingData, strategies: [...strategiesWithoutCustom, value.trim()]});
                               } else {
                                 // If empty, remove from selection
                                 setOnboardingData({...onboardingData, strategies: strategiesWithoutCustom});
                               }
                             }}
                             onClick={(e) => e.stopPropagation()}
                             onKeyDown={(e) => e.stopPropagation()}
                             onFocus={(e) => e.stopPropagation()}
                             onMouseDown={(e) => e.stopPropagation()}
                             className="h-7 text-sm flex-1"
                           />
                     </div>
                       </DropdownMenuContent>
                     </DropdownMenu>
                   </div>

                   <div className="space-y-1.5">
                     <div className="flex items-center justify-between">
                       <Label className="text-sm font-semibold">Asset Focus <span className="text-[10px] text-neutral-400 font-normal">(Recommended max 5)</span></Label>
                       {onboardingData.assets.length > 5 && (
                         <span className="text-[10px] text-amber-600 font-medium italic">⚠️ Over recommended</span>
                       )}
                     </div>
                     <DropdownMenu open={assetDropdownOpen} onOpenChange={setAssetDropdownOpen}>
                       <DropdownMenuTrigger asChild>
                         <Button
                           variant="outline"
                           className="w-full justify-between text-left font-normal h-10"
                         >
                          <span className={onboardingData.assets.length > 0 ? "text-foreground" : "text-muted-foreground"}>
                            {onboardingData.assets.length > 0 
                              ? `${onboardingData.assets.length} selected` 
                              : "Select asset focus..."}
                          </span>
                          <ChevronDown className="h-4 w-4 opacity-50" />
                         </Button>
                       </DropdownMenuTrigger>
                       <DropdownMenuContent className="min-w-[var(--radix-dropdown-menu-trigger-width)] w-[var(--radix-dropdown-menu-trigger-width)]">
                         {[
                           "Low-rise residential", "Mid-rise residential", "High-rise residential", "Purpose-built rental", 
                           "Mixed-use", "Industrial", "Retail", "Office", "Land development", "Institutional"
                         ].map(s => {
                           const isSelected = onboardingData.assets.includes(s);
                           return (
                             <DropdownMenuCheckboxItem
                               key={s}
                               checked={isSelected}
                               onCheckedChange={() => {
                                 if (!onboardingData.assets.includes(s)) {
                                   setOnboardingData({...onboardingData, assets: [...onboardingData.assets, s]});
                                 } else {
                                   setOnboardingData({...onboardingData, assets: onboardingData.assets.filter(x => x !== s)});
                                 }
                               }}
                               onSelect={(e) => e.preventDefault()}
                               className="relative pr-8 pl-2 [&>span:first-child]:hidden"
                             >
                               <span className="flex-1">{s}</span>
                               {isSelected && (
                                 <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
                                   <Check className="h-4 w-4 text-black" />
                                 </span>
                               )}
                             </DropdownMenuCheckboxItem>
                           );
                         })}
                         <div className="relative flex items-center rounded-sm py-1.5 pl-2 pr-2 text-sm outline-none">
                           <Input
                             placeholder="Please specify..."
                             value={customAsset}
                             onChange={(e) => {
                               const value = e.target.value;
                               setCustomAsset(value);
                               
                               // Find any existing custom asset (one that's not in the predefined list)
                               const predefined = ["Low-rise residential", "Mid-rise residential", "High-rise residential", "Purpose-built rental", 
                                 "Mixed-use", "Industrial", "Retail", "Office", "Land development", "Institutional"];
                               const currentAssets = onboardingData.assets;
                               const existingCustom = currentAssets.find(a => !predefined.includes(a));
                               
                               // Remove the existing custom value if it exists
                               const assetsWithoutCustom = existingCustom 
                                 ? currentAssets.filter(x => x !== existingCustom)
                                 : currentAssets;
                               
                               // Only add if there's text
                               if (value.trim()) {
                                 setOnboardingData({...onboardingData, assets: [...assetsWithoutCustom, value.trim()]});
                               } else {
                                 // If empty, remove from selection
                                 setOnboardingData({...onboardingData, assets: assetsWithoutCustom});
                               }
                             }}
                             onClick={(e) => e.stopPropagation()}
                             onKeyDown={(e) => e.stopPropagation()}
                             onFocus={(e) => e.stopPropagation()}
                             onMouseDown={(e) => e.stopPropagation()}
                             className="h-7 text-sm flex-1"
                           />
                     </div>
                       </DropdownMenuContent>
                     </DropdownMenu>
                   </div>

                   <div className="space-y-1.5">
                     <div className="flex items-center justify-between">
                       <Label className="text-sm font-semibold">Primary Decision Context <span className="text-[10px] text-neutral-400 font-normal">(Recommended 2-3)</span></Label>
                       {(onboardingData.contexts?.length || 0) > 3 && (
                         <span className="text-[10px] text-amber-600 font-medium italic">⚠️ Over recommended</span>
                       )}
                     </div>
                     <DropdownMenu open={contextDropdownOpen} onOpenChange={setContextDropdownOpen}>
                       <DropdownMenuTrigger asChild>
                         <Button
                           variant="outline"
                           className="w-full justify-between text-left font-normal h-10"
                         >
                          <span className={(onboardingData.contexts || []).length > 0 ? "text-foreground" : "text-muted-foreground"}>
                            {(onboardingData.contexts || []).length > 0 
                              ? `${(onboardingData.contexts || []).length} selected` 
                              : "Select decision context..."}
                          </span>
                          <ChevronDown className="h-4 w-4 opacity-50" />
                         </Button>
                       </DropdownMenuTrigger>
                       <DropdownMenuContent className="min-w-[var(--radix-dropdown-menu-trigger-width)] w-[var(--radix-dropdown-menu-trigger-width)]">
                         {["Acquisition / underwriting", "Entitlements / rezoning", "Design / site plan", "Financing / capital planning", "Construction / delivery", "Leasing / disposition"].map(s => {
                           const isSelected = (onboardingData.contexts || []).includes(s);
                           return (
                             <DropdownMenuCheckboxItem
                               key={s}
                               checked={isSelected}
                               onCheckedChange={() => {
                         const current = onboardingData.contexts || [];
                                 if (!current.includes(s)) {
                                   setOnboardingData({...onboardingData, contexts: [...current, s]});
                                 } else {
                                   setOnboardingData({...onboardingData, contexts: current.filter(x => x !== s)});
                                 }
                               }}
                               onSelect={(e) => e.preventDefault()}
                               className="relative pr-8 pl-2 [&>span:first-child]:hidden"
                             >
                               <span className="flex-1">{s}</span>
                               {isSelected && (
                                 <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
                                   <Check className="h-4 w-4 text-black" />
                                 </span>
                               )}
                             </DropdownMenuCheckboxItem>
                           );
                         })}
                         <div className="relative flex items-center rounded-sm py-1.5 pl-2 pr-2 text-sm outline-none">
                           <Input
                             placeholder="Please specify..."
                             value={customContext}
                             onChange={(e) => {
                               const value = e.target.value;
                               setCustomContext(value);
                               
                               // Find any existing custom context (one that's not in the predefined list)
                               const predefined = ["Acquisition / underwriting", "Entitlements / rezoning", "Design / site plan", "Financing / capital planning", "Construction / delivery", "Leasing / disposition"];
                               const current = onboardingData.contexts || [];
                               const existingCustom = current.find(c => !predefined.includes(c));
                               
                               // Remove the existing custom value if it exists
                               const contextsWithoutCustom = existingCustom 
                                 ? current.filter(x => x !== existingCustom)
                                 : current;
                               
                               // Only add if there's text
                               if (value.trim()) {
                                 setOnboardingData({...onboardingData, contexts: [...contextsWithoutCustom, value.trim()]});
                               } else {
                                 // If empty, remove from selection
                                 setOnboardingData({...onboardingData, contexts: contextsWithoutCustom});
                               }
                             }}
                             onClick={(e) => e.stopPropagation()}
                             onKeyDown={(e) => e.stopPropagation()}
                             onFocus={(e) => e.stopPropagation()}
                             onMouseDown={(e) => e.stopPropagation()}
                             className="h-7 text-sm flex-1"
                           />
                         </div>
                       </DropdownMenuContent>
                     </DropdownMenu>
                   </div>

                   <div className="space-y-1.5">
                     <div className="flex items-center justify-between">
                       <Label className="text-sm font-semibold">Urgency/Time Horizon <span className="text-[10px] text-neutral-400 font-normal">(Optional)</span></Label>
                     </div>
                     <DropdownMenu open={urgencyDropdownOpen} onOpenChange={setUrgencyDropdownOpen}>
                       <DropdownMenuTrigger asChild>
                         <Button
                           variant="outline"
                           className="w-full justify-between text-left font-normal h-10"
                         >
                           <span>{(onboardingData.urgencyTimeHorizon && onboardingData.urgencyTimeHorizon.length > 0) ? onboardingData.urgencyTimeHorizon[0] : "Select time horizon..."}</span>
                           <ChevronDown className="h-4 w-4 opacity-50" />
                         </Button>
                       </DropdownMenuTrigger>
                       <DropdownMenuContent className="min-w-[var(--radix-dropdown-menu-trigger-width)] w-[var(--radix-dropdown-menu-trigger-width)]">
                         {["0-6 Months", "6-18 Months", "18-36 Months", "Both active & early signals"].map(s => {
                           const isSelected = (onboardingData.urgencyTimeHorizon || []).includes(s);
                           return (
                            <DropdownMenuCheckboxItem
                              key={s}
                              checked={isSelected}
                              onCheckedChange={() => {
                                if (isSelected) {
                                  // Deselect if already selected
                                  setOnboardingData({...onboardingData, urgencyTimeHorizon: []});
                                } else {
                                  // Select this option (clear custom field when selecting predefined option)
                                  setCustomUrgency("");
                                  setOnboardingData({...onboardingData, urgencyTimeHorizon: [s]});
                                }
                                // Close dropdown after single selection
                                setUrgencyDropdownOpen(false);
                              }}
                              className="relative pr-8 pl-2 [&>span:first-child]:hidden"
                            >
                              <span className="flex-1">{s}</span>
                              {isSelected && (
                                <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
                                  <Check className="h-4 w-4 text-black" />
                                </span>
                              )}
                            </DropdownMenuCheckboxItem>
                           );
                         })}
                         <div className="relative flex items-center rounded-sm py-1.5 pl-2 pr-2 text-sm outline-none">
                           <Input
                             placeholder="Please specify..."
                             value={customUrgency}
                             onChange={(e) => {
                               const value = e.target.value;
                               setCustomUrgency(value);
                               // Only add if there's text, otherwise clear selection
                               if (value.trim()) {
                                 setOnboardingData({...onboardingData, urgencyTimeHorizon: [value.trim()]});
                               } else {
                                 setOnboardingData({...onboardingData, urgencyTimeHorizon: []});
                               }
                             }}
                             onClick={(e) => e.stopPropagation()}
                             onKeyDown={(e) => e.stopPropagation()}
                             onFocus={(e) => e.stopPropagation()}
                             onMouseDown={(e) => e.stopPropagation()}
                             className="h-7 text-sm flex-1"
                           />
                         </div>
                       </DropdownMenuContent>
                     </DropdownMenu>
                   </div>
                 </div>

                 <div className="mt-auto flex justify-between items-center pt-4 border-t border-neutral-100 dark:border-neutral-800 shrink-0">
                    <Button variant="ghost" className="h-10" onClick={() => setStep("intro")}>Back</Button>
                    <Button 
                      onClick={handleNext} 
                      disabled={
                        !onboardingData.businessRole || 
                        onboardingData.businessRole.trim() === "" ||
                        onboardingData.strategies.length === 0 ||
                        onboardingData.assets.length === 0 ||
                        (onboardingData.contexts || []).length === 0
                      } 
                      className="h-10 px-6 font-semibold gap-2"
                    >
                      Continue <ChevronRight className="h-4 w-4" />
                    </Button>
                 </div>
              </motion.div>
            )}

            {step === "team" && (
              <motion.div 
                key="team"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="flex-1 flex flex-col p-10 h-full overflow-hidden"
              >
                 <div className="mb-6 shrink-0">
                   <Badge className="mb-2 bg-primary/10 text-primary border-none">Step 2 of 3</Badge>
                   <h2 className="text-2xl font-bold text-neutral-900 dark:text-white">Invite your team</h2>
                   <p className="text-neutral-500 text-sm mt-1 leading-relaxed">
                     Add colleagues to your shared workspace. Team members see the same monitored municipalities and alerts — no duplicate setup.
                   </p>
                 </div>

                 {subscriptionTier === "solo" ? (
                   <div className="flex-1 flex flex-col items-center justify-center p-8 bg-neutral-50 dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800">
                     <div className="text-center space-y-4 max-w-md">
                       <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                         <Building2 className="h-8 w-8 text-primary" />
                       </div>
                       <div>
                         <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">
                           Solo plan - Team invites not available
                         </h3>
                         <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
                           Your Solo plan allows only one user (you). To invite team members, upgrade to the Org plan which supports up to 3 users.
                         </p>
                         <Button 
                           variant="outline" 
                           className="mt-4"
                           onClick={() => setShowUpgradeDialog(true)}
                         >
                           Upgrade to Org Plan
                         </Button>
                       </div>
                     </div>
                   </div>
                 ) : (
                   <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2 hide-scrollbar flex-1 min-h-0">
                      {/* Limit to max 2 invites */}
                      {onboardingData.invites.slice(0, 2).map((invite, idx) => (
                        <div key={idx} className="flex gap-2 items-start">
                          <Input 
                            placeholder="colleague@company.com" 
                            value={invite.email} 
                            onChange={(e) => {
                              const next = [...onboardingData.invites];
                              next[idx].email = e.target.value;
                              setOnboardingData({...onboardingData, invites: next});
                            }}
                            className="h-11 flex-1"
                          />
                          <div className="space-y-1">
                            <Select value={invite.role} onValueChange={(v) => {
                              const next = [...onboardingData.invites];
                              next[idx].role = v;
                              setOnboardingData({...onboardingData, invites: next});
                            }}>
                              <SelectTrigger className="w-[110px] h-11">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Admin">Admin</SelectItem>
                                <SelectItem value="Manager">Manager</SelectItem>
                                <SelectItem value="Analyst">Analyst</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      ))}
                      {onboardingData.invites.length < 2 && (
                        <Button 
                          variant="outline" 
                          className="w-full border-dashed border-2 h-11 text-neutral-500"
                          onClick={() => {
                            if (onboardingData.invites.length < 2) {
                              setOnboardingData({
                                ...onboardingData, 
                                invites: [...onboardingData.invites, {email: "", role: "Analyst"}]
                              });
                            }
                          }}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add member
                        </Button>
                      )}
                      {onboardingData.invites.length >= 2 && (
                        <p className="text-xs text-neutral-500 text-center py-2">
                          Maximum of 2 team members allowed (Org plan limit: 3 users total)
                        </p>
                      )}
                      <p className="text-[10px] text-neutral-400 text-center mt-2">
                        Permissions control who can manage settings vs. view insights.
                      </p>
                   </div>
                 )}

                 <div className="mt-auto flex flex-col items-center gap-4 pt-6 border-t border-neutral-100 dark:border-neutral-800 shrink-0">
                    <p className="text-[11px] text-neutral-400">
                      You can invite teammates later from Settings.
                    </p>
                    <div className="flex justify-between items-center w-full">
                      <Button variant="ghost" className="h-10" onClick={() => setStep("profile")}>Back</Button>
                      <div className="flex gap-3">
                        <Button variant="ghost" className="h-10" onClick={handleNext}>Skip</Button>
                        <Button onClick={handleNext} className="h-10 px-6 font-semibold gap-2">
                          Continue <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                 </div>
              </motion.div>
            )}

            {step === "priorities" && (
              <motion.div 
                key="priorities"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="flex-1 flex flex-col p-10 h-full overflow-hidden"
              >
                 <div className="mb-4 shrink-0">
                   <Badge className="mb-2 bg-primary/10 text-primary border-none">Step 3 of 3</Badge>
                   <h2 className="text-2xl font-bold text-neutral-900 dark:text-white">What should we prioritize?</h2>
                   
                   <div className="mt-3 p-3 bg-neutral-50 dark:bg-neutral-900 rounded-lg border border-neutral-100 dark:border-neutral-800">
                     <p className="text-[12px] leading-relaxed text-neutral-600 dark:text-neutral-400">
                       <span className="font-semibold text-neutral-900 dark:text-white">Industry Pulse</span> is your ongoing feed of policy changes, approvals activity, and decisions across your monitored municipalities.
                     </p>
                     <p className="text-[11px] mt-2 text-neutral-500 italic">
                       These selections simply help us highlight and rank the items that matter most to you. Cityscrape always monitors all categories.
                     </p>
                   </div>
                 </div>

                 <div className="flex-1 overflow-y-auto pr-2 -mr-2 space-y-2 mb-4 min-h-0 hide-scrollbar">
                    {loadingCategories ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
                      </div>
                    ) : defaultCategories.length === 0 ? (
                      <div className="text-center py-8 text-sm text-neutral-500">
                        No categories available
                      </div>
                    ) : (
                      defaultCategories.map(cat => (
                        <label key={cat.id} className={cn(
                        "flex items-center gap-3 p-3 rounded-lg border-2 transition-all cursor-pointer group",
                          onboardingData.priorities.includes(cat.slug) 
                          ? "border-primary bg-primary/5 shadow-sm" 
                          : "border-neutral-50 dark:border-neutral-900 hover:border-neutral-100"
                      )}>
                        <Checkbox 
                            checked={onboardingData.priorities.includes(cat.slug)}
                            onCheckedChange={() => togglePriority(cat.slug)}
                        />
                          <span className={cn("text-xs font-medium flex-1", onboardingData.priorities.includes(cat.slug) ? "text-primary" : "text-neutral-700 dark:text-neutral-300")}>
                            {cat.display_name || cat.name}
                        </span>
                        {cat.description && (
                          <TooltipProvider>
                            <Tooltip delayDuration={0}>
                              <TooltipTrigger asChild onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                                <Info className="h-4 w-4 text-neutral-400 group-hover:text-neutral-600 dark:group-hover:text-neutral-300 transition-colors flex-shrink-0" />
                              </TooltipTrigger>
                              <TooltipContent side="right" className="max-w-xs">
                                <p className="text-xs">{cat.description}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </label>
                      ))
                    )}
                 </div>

                 <div className="mt-auto flex justify-between items-center shrink-0 pt-4 border-t border-neutral-100 dark:border-neutral-800">
                    <Button variant="ghost" className="h-10" onClick={() => {
                      // Go back to team if not solo, otherwise go back to profile
                      if (subscriptionTier === "solo") {
                        setStep("profile");
                      } else {
                        setStep("team");
                      }
                    }}>Back</Button>
                    <Button onClick={handleNext} className="h-10 px-8 font-semibold bg-primary hover:bg-primary/90 text-neutral-900">
                      Complete Setup <Check className="ml-2 h-4 w-4" />
                    </Button>
                 </div>
              </motion.div>
            )}

            {step === "finish" && (
              <motion.div 
                key="finish"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex-1 flex flex-col items-center justify-center p-10 text-center h-full"
              >
                 <div className="h-24 w-24 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-6 shadow-xl shadow-green-500/10">
                    <CheckCircle2 className="h-12 w-12 text-green-600 dark:text-green-400" />
                 </div>
                 
                 <h2 className="text-3xl font-bold text-neutral-900 dark:text-white mb-2">You're all set!</h2>
                 <p className="text-neutral-500 max-w-sm mx-auto mb-8 text-base">
                   Your account has been fully personalized. Welcome to Cityscrape.
                 </p>

                 <Button size="lg" onClick={handleNext} className="w-full sm:w-auto min-w-[240px] h-14 text-lg font-bold gap-2 bg-primary hover:bg-primary/90 text-neutral-900 shadow-xl shadow-primary/20">
                   Go to Dashboard <ArrowUpRight className="h-5 w-5" />
                 </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
      
      {/* Upgrade Confirmation Dialog */}
      <AlertDialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Upgrade to Org Plan</AlertDialogTitle>
            <AlertDialogDescription>
              Upgrade from Solo ($300/mo) to Org Plan ($750/mo) to invite up to 2 additional team members.
              <br /><br />
              You'll be charged a prorated amount for the remainder of your billing period. The upgrade will take effect immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUpgrading}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleUpgradeToOrg}
              disabled={isUpgrading}
              className="bg-primary hover:bg-primary/90"
            >
              {isUpgrading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Upgrading...
                </>
              ) : (
                "Confirm Upgrade"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
